import React, { useEffect, useState } from "react";
import {
  getTenantById,
  getTenantOverviewKeyMilestones,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import { RiDownloadLine } from "react-icons/ri";

import { saveAs } from "file-saver";


export interface WakefitInsights {
  citations: string[];
  search_results: SearchResult[];
  content: CompanyContent;
}

export interface SearchResult {
  title: string;
  url: string;
  date: string | null;
  last_updated: string;
}

export interface CompanyContent {
  company: string;
  website: string;
  milestones: Milestone[];
  notable_facts: string[];
}

export interface Milestone {
  period?: string;
  year?: number;
  details?: string;
  month?: string;
  event: string;
  sources: string[];
}

interface KeyMilestonesProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

const SkeletonMilestone = () => (
  <div className="relative flex items-start mb-12 animate-pulse">
    {/* Left Column Skeleton */}
    <div className="absolute -left-40 w-36 text-right pr-4">
      <div className="h-4 bg-gray-300 rounded w-24 mb-2"></div>
      <div className="h-3 bg-gray-300 rounded w-32"></div>
    </div>

    {/* Dot */}
    <div className="absolute left-0 top-1.5 -ml-[3px] w-4 h-4 rounded-full bg-gray-200 shadow" />

    {/* Right Column Skeleton */}
    <div className="ml-6 max-w-4xl">
      <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
      <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>

      <div className="flex space-x-2">
        <div className="h-6 w-16 bg-gray-300 rounded-full"></div>
        <div className="h-6 w-20 bg-gray-300 rounded-full"></div>
        <div className="h-6 w-14 bg-gray-300 rounded-full"></div>
      </div>
    </div>
  </div>
);

export const KeyMilestones: React.FC<KeyMilestonesProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<WakefitInsights | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<boolean>(false);
  const [downloadingWord, setDownloadingWord] = useState<boolean>(false);
  const [tenant, setTenant] = useState<Tenant | null>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };
    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      try {
        const response = await getTenantOverviewKeyMilestones(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        console.log("Tenant Data: key milestone", response);
        setData(response);
      } catch (error) {
        console.error("Error fetching tenant overview:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };
  const downloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Page dimensions
      const marginX = 20;
      const marginY = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const contentWidth = pageWidth - marginX * 2;

      // Add header with full width line
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Key Milestones", marginX, marginY);

      // Add separator line
      doc.setDrawColor(200);
      doc.setLineWidth(0.2);
      doc.line(marginX, marginY + 3, pageWidth - marginX, marginY + 3);

      // Add generation date
      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      const dateText = `Generated on: ${new Date().toLocaleDateString()}`;
      doc.text(dateText, marginX, marginY + 10);

      // Add ID info
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const idText = `Tenant ID: ${tenantId}`;
      doc.text(idText, marginX, marginY + 15);

      // Add timeline main line
      const timelineX = marginX + 10;
      doc.setDrawColor(100);
      doc.setLineWidth(0.5);
      // doc.setLineDashPattern([3, 3], 0);

      // Start content positioning
      let currentY = marginY + 25;

      // Add content with proper page handling
      sortedMilestones.forEach((milestone) => {
        // Check for page break
        if (currentY > pageHeight - 30) {
          doc.addPage();
          currentY = marginY + 25;
        }

        // Year bubble
        if (milestone.year) {
          doc.setFillColor(240, 240, 240);
          doc.setDrawColor(200);
          doc.roundedRect(timelineX - 15, currentY - 6, 18, 8, 3, 3, "FD");

          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(80, 80, 80);
          doc.text(`${milestone.year}`, timelineX - 6, currentY - 1, {
            align: "center",
          });
        }

        // Event title
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(50, 50, 50);
        doc.text(milestone.event, timelineX + 8, currentY + 1);

        // Details
        if (milestone.details) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(80, 80, 80);
          const detailsLines = doc.splitTextToSize(
            milestone.details,
            contentWidth - 35
          );
          doc.text(detailsLines, timelineX + 8, currentY + 7);
          currentY += detailsLines.length * 6;
        }

        // Source links if available
        if (milestone.sources && milestone.sources.length > 0) {
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(0, 0, 255);
          const sourceText = `Source: ${milestone.sources[0]}`;
          const displayText =
            sourceText.length > 80
              ? sourceText.substring(0, 77) + "..."
              : sourceText;
          doc.text(displayText, timelineX + 8, currentY + 7);
          // Add link annotation
          doc.link(
            timelineX + 8,
            currentY + 4,
            doc.getTextWidth(displayText),
            5,
            { url: milestone.sources[0] }
          );
          currentY += 6;
        }

        currentY += 15; // Vertical spacing between milestones
      });

      // Load watermark and add to all pages
      if (tenant?.logoUrl) {
        try {
          const loadImage = (url: string): Promise<HTMLImageElement> => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = "Anonymous";
              img.onload = () => resolve(img);
              img.onerror = (e) => {
                console.warn("Failed to load watermark image:", e);
                reject(e);
              };
              img.src = url;
            });
          };

          const watermarkImg = await loadImage(tenant.logoUrl);

          // Add watermark to all pages
          const totalPages = doc.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);

            doc.saveGraphicsState();
            doc.setGState(new (doc.GState as any)({ opacity: 0.1 }));

            const watermarkWidth = 80;
            const watermarkHeight =
              (watermarkImg.height / watermarkImg.width) * watermarkWidth;
            const xPos = (pageWidth - watermarkWidth) / 2;
            const yPos = (pageHeight - watermarkHeight) / 2;

            doc.addImage(
              watermarkImg,
              "PNG",
              xPos,
              yPos,
              watermarkWidth,
              watermarkHeight
            );

            doc.restoreGraphicsState();
          }
        } catch (error) {
          console.warn("Failed to add watermark:", error);
        }
      }

      // Add footer to all pages
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      // Save the document
      doc.save(`key-milestones-${tenantId}-${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setDownloadingPdf(false);
    }
  };

const downloadWord = async () => {
  setDownloadingWord(true);
  try {
    const {
      Document,
      Paragraph,
      TextRun,
      HeadingLevel,
      AlignmentType,
      Table,
      TableRow,
      TableCell,
      WidthType,
      Footer,
      Packer,
      convertInchesToTwip,
      PageOrientation,
      ImageRun,
    } = await import("docx");

    const base64ToUint8Array = (base64: string): Uint8Array => {
      const binaryString = window.atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    };

    // Load logo image if logoUrl exists
    let logoImageData: {
      data: Uint8Array;
      mimeType: "jpg" | "png" | "gif" | "bmp";
    } | null = null;

    if (tenant?.logoUrl) {
      try {
        const response = await fetch(tenant.logoUrl);
        const blob = await response.blob();

        // Map MIME type to docx accepted format
        const getMimeType = (
          blobType: string
        ): "jpg" | "png" | "gif" | "bmp" => {
          if (blobType.includes("png")) return "png";
          if (blobType.includes("jpeg") || blobType.includes("jpg")) return "jpg";
          if (blobType.includes("gif")) return "gif";
          if (blobType.includes("bmp")) return "bmp";
          return "png";
        };

        const mimeType = getMimeType(blob.type);
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            const base64Data = result.split(",")[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const uint8Array = base64ToUint8Array(base64);
        logoImageData = { data: uint8Array, mimeType };
      } catch (error) {
        console.error("Failed to load logo image:", error);
      }
    }

    const milestonesTableRows = sortedMilestones.map((milestone) => {
      return new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                text: milestone.year?.toString() || "-",
                style: "tableCell",
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: milestone.event,
                style: "tableCell",
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                text: milestone.details || "-",
                style: "tableCell",
              }),
            ],
          }),
        ],
      });
    });

    // Create footer content
    const footerContent= [];

    // Add confidential text
    footerContent.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Confidential - Generated by ${tenant?.name}`,
            size: 10,
            color: "888888",
          }),
        ],
        alignment: AlignmentType.CENTER,
      })
    );

    // Add logo if available
    if (logoImageData) {
      footerContent.push(
        new Paragraph({
          children: [
            new ImageRun({
              data: logoImageData.data,
              type: logoImageData.mimeType,
              transformation: {
                width: 60,
                height: 60,
              },
            }),
          ],
          alignment: AlignmentType.RIGHT,
          spacing: { before: 100 },
        })
      );
    }

    const doc = new Document({
      creator: "Your Application",
      title: "Key Milestones",
      description: "Key milestones document",
      styles: {
        paragraphStyles: [
          {
            id: "tableCell",
            name: "TableCell",
            basedOn: "Normal",
            next: "Normal",
            run: {
              size: 10,
              font: "Arial",
            },
            paragraph: {
              spacing: {
                after: 100,
                before: 100,
              },
              alignment: AlignmentType.LEFT,
            },
          },
        ],
      },
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(1),
                right: convertInchesToTwip(0.75),
                bottom: convertInchesToTwip(1),
                left: convertInchesToTwip(0.75),
              },
              size: {
                orientation: PageOrientation.PORTRAIT,
              },
            },
          },
          children: [
            new Paragraph({
              text: "Key Milestones",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
              text: `Generated on: ${new Date().toLocaleDateString()}`,
              alignment: AlignmentType.CENTER,
            }),
            new Table({
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: [new Paragraph({ text: "Year" })],
                      shading: {
                        fill: "4472C4",
                      },
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "Event" })],
                      shading: {
                        fill: "4472C4",
                      },
                    }),
                    new TableCell({
                      children: [new Paragraph({ text: "Details" })],
                      shading: {
                        fill: "4472C4",
                      },
                    }),
                  ],
                }),
                ...milestonesTableRows,
              ],
              width: {
                size: 100,
                type: WidthType.PERCENTAGE,
              },
              columnWidths: [1500, 3000, 4500],
            }),
          ],
          footers: {
            default: new Footer({
              children: footerContent,
            }),
          },
        },
      ],
    });

    // Generate and download
    const blob = await Packer.toBlob(doc);
    const fileName = `key-milestones-${tenantId}-${new Date().getTime()}.docx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error("Error generating Word document:", error);
  } finally {
    setDownloadingWord(false);
  }
};


  if (loading) {
    // Show 5 skeleton items while loading
    return (
      <div className="bg-white py-6 rounded-2xl shadow-md border border-gray-300 mt-0 2xl:mt-20 max-h-[65vh] max-w-4xl mx-auto overflow-y-auto px-6">
        <div className="relative ml-40 before:absolute before:top-0 before:bottom-0 before:left-1 before:w-0.5 before:border-l before:border-dashed before:border-gray-300">
          {[...Array(5)].map((_, i) => (
            <SkeletonMilestone key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-red-500">Failed to load data.</div>;
  }

  const sortedMilestones = [...data.content.milestones].sort((a, b) => {
    const yearA = a.year ?? 0;
    const yearB = b.year ?? 0;
    return yearB - yearA; // Descending by year
  });

  return (
    <div className="bg-[#FDFDFD] py-6 rounded-2xl shadow-md border border-gray-300 mt-0 2xl:mt-20 max-w-4xl mx-auto  px-6">
      <div className="flex gap-2 justify-end">
        <button
          onClick={downloadPDF}
          disabled={downloadingPdf}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingPdf ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" />
              PDF
            </>
          )}
        </button>

        <button
          onClick={downloadWord}
          disabled={downloadingWord}
          className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          {downloadingWord ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating...
            </>
          ) : (
            <>
              <RiDownloadLine className="w-4 h-4" />
              Word
            </>
          )}
        </button>
      </div>

      <div className="relative ml-50 before:absolute before:top-0 before:bottom-0 before:left-1 before:w-0.5 before:border-l before:border-dashed before:border-gray-300">
        {sortedMilestones.map((milestone, index) => (
          <div key={index} className="relative flex items-start mb-6">
            {/* Left Column: Date */}
            <div className="absolute -left-45 w-46 text-right pr-4 ">
              {milestone.year && (
                <p className="text-sm text-gray-500 mt-1 mb-1">
                  {milestone.year}
                </p>
              )}
              {data && (
                <p className="text-sm capitalize font-medium text-slate-900 mb-3">
                  {milestone.event}{" "}
                </p>
              )}
            </div>

            {/* Dot */}
            <div className="absolute left-1 top-8 -ml-[3px] w-2 h-2 rounded-full bg-gray-200 shadow" />

            {/* Right Column: Event & Source */}
            <div className="ml-5 text-gray-700 max-w-4xl text-sm ">
              <p className="mb-1 mt-7 ">
                {milestone.details}
                {milestone?.sources?.map((sourceUrl, i) => {
                  const url = new URL(sourceUrl);
                  const display = url.hostname.replace("www.", "");
                  return (
                    <button
                      key={i}
                      onClick={(e) => handleSourceClick(e, sourceUrl)}
                      className="text-xs text-gray-700 border ml-1  border-gray-300 font-medium px-2 py-1 rounded-full hover:bg-gray-50 cursor-pointer"
                    >
                      {display}
                    </button>
                  );
                })}
              </p>
            </div>
          </div>
        ))}
      </div>
      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </div>
  );
};
